import { notifications } from "@mantine/notifications";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";

import { showErrorToast, showSuccessToast } from "#app/notifications";
import { formatErrorWithLocation } from "#modules/materials/helpers/materialErrorFormatter";
import { materialsQuery } from "#modules/materials/infra/materials.query";
import { uploadCustomMaterials } from "#modules/materials/infra/uploadCustomMaterials.api";
import { Anchor } from "#shared/view/components/Anchor";
import { MaterialUploadResponse, MaterialUploadError, MaterialPriceAlert } from "shared";

const PRICE_ALERT_TOAST_ID = "material-price-alert-toast";

export const useUploadCustomMaterials = (
  onError?: (errors: MaterialUploadError[]) => void,
  onPriceAlerts?: (alerts: MaterialPriceAlert[]) => void,
) => {
  const queryClient = useQueryClient();
  const { t } = useTranslation();

  const { mutate, ...rest } = useMutation({
    mutationFn: uploadCustomMaterials,
    onSuccess: (response) => {
      if (response.alerts && response.alerts.length > 0) {
        const alerts = response.alerts;

        notifications.show({
          id: PRICE_ALERT_TOAST_ID,
          withCloseButton: true,
          autoClose: false,
          color: "orange",
          title: t("settings.materials.upload.priceAlertTitle", { count: alerts.length }),
          message: (
            <Anchor
              component="button"
              type="button"
              c="orange.9"
              onClick={() => {
                onPriceAlerts?.(alerts);
                notifications.hide(PRICE_ALERT_TOAST_ID);
              }}
            >
              {t("settings.materials.upload.priceAlertLink")}
            </Anchor>
          ),
        });
      } else {
        showSuccessToast({
          title: t("settings.materials.upload.successMessage"),
        });
      }
      void queryClient.invalidateQueries(materialsQuery());
    },
    onError: (error: unknown) => {
      let uploadError: MaterialUploadResponse | null = null;

      const isMaterialUploadResponse =
        error && typeof error === "object" && "success" in error && "errors" in error;

      if (isMaterialUploadResponse) {
        uploadError = error as MaterialUploadResponse;
      } else if (error && typeof error === "object" && error instanceof Error) {
        for (const value of Object.values(error)) {
          if (value && typeof value === "object" && "success" in value && "errors" in value) {
            uploadError = value as MaterialUploadResponse;
            break;
          }
        }
      } else if (error && typeof error === "object") {
        const errorObj = error as Record<string, unknown>;
        const possiblePaths = ["response", "data", "body", "error"];

        for (const path of possiblePaths) {
          const nested = errorObj[path];
          if (nested && typeof nested === "object" && "success" in nested && "errors" in nested) {
            uploadError = nested as MaterialUploadResponse;
            break;
          }
        }
      }

      if (
        uploadError &&
        uploadError.errors &&
        uploadError.errors.length > 0 &&
        uploadError.errors[0]
      ) {
        if (onError) {
          onError(uploadError.errors);
        }

        const primaryError = formatErrorWithLocation(uploadError.errors[0], t);

        if (uploadError.errors.length > 1) {
          const errorList = uploadError.errors
            .slice(0, 5)
            .map((err) => formatErrorWithLocation(err, t))
            .join("\n");

          showErrorToast({
            title: t("settings.materials.upload.validationErrors"),
            message: errorList,
            autoClose: 10000,
          });
        } else {
          showErrorToast({
            title: primaryError,
          });
        }
        return;
      }

      showErrorToast({
        title: t("settings.materials.upload.failedToUpload"),
      });
    },
  });

  return { uploadCustomMaterials: mutate, ...rest };
};
